/**
 * HandPanel.tsx — WEB.4.UI.3
 *
 * 290px right-side panel showing current seat's hand + dice/turn controls.
 * Collapsible to 56px tab. Card slots dispatch openCardDetail on click.
 * Roll Dice / End Turn buttons replace ActionStrip in App.tsx.
 *
 * VISIBLE WHEN: always (collapses to 56px tab, never fully unmounts)
 * TEST PATH: game starts → panel visible on right side of HexBoard
 */

import { MAX_HAND_SIZE } from "@gdg/rules-engine";
import type { AuraManagerState } from "@gdg/rules-engine";
import type React from "react";
import {
  SEAT_COLORS as SEAT_HEX_COLORS,
  getCardById,
  useGameStore,
} from "../../store/game-store.js";
import type { CardInHand, SeatIndex } from "../../store/game-store.js";
import styles from "./HandPanel.module.css";

/** R7 #1: detect aura/passive cards (mirrors `_isAuraCard` in game-store). */
function isHandCardAura(card: CardInHand): boolean {
  const def = getCardById(card.id);
  if (!def) return false;
  if (def.discardMode === "Whenever") return true;
  if (def.effects?.some((e) => e.kind === "AuraApply")) return true;
  return false;
}

/**
 * R18-FC-5: Returns true when the card has AuraApply effects AND at least one
 * aura from this card is currently registered for the given seat in auraState.
 * Mirrors `_cardHasActiveAura` in HandSlotDrawer.tsx (not exported — recreated
 * inline per brief rule). Per-seat only — NO bySeat[-1] lookup (NO GLOBAL AURA).
 */
function _cardHasActiveAura(card: CardInHand, seat: number, auraState: AuraManagerState): boolean {
  const def = getCardById(card.id);
  if (def === undefined) return false;
  const hasAuraApply = def.effects.some((e) => e.kind === "AuraApply");
  if (!hasAuraApply) return false;
  const seatAuras = auraState.bySeat[String(seat)] ?? [];
  return seatAuras.some((a) => a.sourceCardName === def.name);
}

// ---------------------------------------------------------------------------
// Seat config
// ---------------------------------------------------------------------------

const SEAT_COLORS = ["P1", "P2", "P3", "P4"] as const;
const SEAT_NAME_COLORS = ["seatLabelP1", "seatLabelP2", "seatLabelP3", "seatLabelP4"] as const;
const SEAT_HEADER_COLORS = ["headerP1", "headerP2", "headerP3", "headerP4"] as const;
const SEAT_DISPLAY_NAMES = ["Player Red", "Player Blue", "Player Green", "Player Yellow"] as const;

// ---------------------------------------------------------------------------
// Card type → CSS suffix helper
// ---------------------------------------------------------------------------

function cardTypeClass(cardType: string): string {
  switch (cardType) {
    case "Event":
      return "Event";
    case "Policy":
      return "Policy";
    case "Mitigate":
      return "Mitigate";
    case "Tech":
      return "Tech";
    default:
      return "Event";
  }
}

// ---------------------------------------------------------------------------
// SlotFilled — a card in hand
// ---------------------------------------------------------------------------

interface SlotFilledProps {
  card: CardInHand;
  animDelay: number;
  onOpen: (card: CardInHand) => void;
  /** R18-FC-5: true when this card's passive aura is currently active for this seat. */
  auraActive: boolean;
}

function SlotFilled({ card, animDelay, onOpen, auraActive }: SlotFilledProps): React.ReactElement {
  const cardDef = getCardById(card.id);
  const typeSuffix = cardTypeClass(cardDef?.cardType ?? "Event");
  const isAura = cardDef?.discardMode === "Whenever";

  const slotClass = [styles.cardSlot, styles[`cardSlot${typeSuffix}` as keyof typeof styles] ?? ""]
    .filter(Boolean)
    .join(" ");

  const stripClass = [styles.typeStrip, styles[`strip${typeSuffix}` as keyof typeof styles] ?? ""]
    .filter(Boolean)
    .join(" ");

  const effectText = cardDef?.description ?? "";

  const discardLabel =
    cardDef?.discardMode === "Instantly"
      ? "INSTANT"
      : cardDef?.discardMode === "AfterUse"
        ? "INSTANT"
        : cardDef?.discardMode === "Whenever"
          ? "AURA"
          : cardDef?.discardMode === "AfterBuild"
            ? "BUILD"
            : "HOLD";

  const buyCost = cardDef?.buyCost ?? 0;

  return (
    <button
      type="button"
      className={slotClass}
      style={{ animationDelay: `${animDelay}s` }}
      onClick={() => onOpen(card)}
      title="Click → CardDetailModal"
      data-testid={`card-slot-${card.instanceId}`}
    >
      <div className={stripClass}>
        {typeSuffix.toUpperCase()}
        {/* R18-FC-5: show badge when aura is live in registry (auraActive) OR
            card is a Whenever-type aura card (isAura fallback). */}
        {(auraActive || isAura) && (
          <span
            className={styles.auraBadge}
            data-testid={`hand-panel-aura-badge-${card.instanceId}`}
          >
            {auraActive ? "AURA ACTIVE" : "AURA"}
          </span>
        )}
      </div>
      <div className={styles.slotBody}>
        <div className={styles.slotName}>{cardDef?.cardName ?? card.id}</div>
        {effectText && <div className={styles.slotEffect}>{effectText}</div>}
      </div>
      <div className={styles.slotFooter}>
        <div className={styles.slotCost}>
          {buyCost > 0 && (
            <>
              <div className={styles.coinIcon} />
              <span>
                {buyCost} {buyCost === 1 ? "token" : "tokens"}
              </span>
            </>
          )}
        </div>
        <div className={styles.slotMode}>{discardLabel}</div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// SlotEmpty — placeholder slot
// ---------------------------------------------------------------------------

function SlotEmpty({ animDelay }: { animDelay: number }): React.ReactElement {
  return (
    <div
      className={[styles.cardSlot, styles.cardSlotEmpty].join(" ")}
      style={{ animationDelay: `${animDelay}s` }}
      data-testid="card-slot-empty"
    >
      <div className={styles.emptyLabel}>
        ( drawing
        <span className={styles.dot}>.</span>
        <span className={styles.dot}>.</span>
        <span className={styles.dot}>.</span>)
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HandPanel — public component
// ---------------------------------------------------------------------------

export type HandPanelProps = {
  className?: string;
  /** "floating": renders as a fixed right-edge overlay (inside SidescrollerScene). Default "normal". */
  variant?: "normal" | "floating";
};

export function HandPanel({
  className,
  variant = "normal",
}: HandPanelProps = {}): React.ReactElement {
  const currentSeat = useGameStore((s) => s.currentSeat);
  const hands = useGameStore((s) => s.hands);
  const handPanelCollapsed = useGameStore((s) => s.handPanelCollapsed);
  const lastDiceRoll = useGameStore((s) => s.lastDiceRoll);
  // R18-FC-5: read auraState to power _cardHasActiveAura per-card badge.
  const auraState = useGameStore((s) => s.auraState);

  const openCardDetail = useGameStore((s) => s.openCardDetail);
  const toggleHandPanel = useGameStore((s) => s.toggleHandPanel);

  const seatIdx = currentSeat as SeatIndex;
  const hand = hands[seatIdx] ?? [];
  const handCount = hand.length;
  const MAX_SLOTS = MAX_HAND_SIZE;

  const panelClass = [
    styles.panel,
    handPanelCollapsed ? styles.panelCollapsed : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const headerSuffix = SEAT_HEADER_COLORS[seatIdx] ?? "headerP1";
  const headerClass = [
    styles.header,
    styles[headerSuffix as keyof typeof styles] ?? "",
    handPanelCollapsed ? styles.headerHidden : "",
  ]
    .filter(Boolean)
    .join(" ");

  const labelSuffix = SEAT_NAME_COLORS[seatIdx] ?? "seatLabelP1";
  const labelClass = [styles.seatLabel, styles[labelSuffix as keyof typeof styles] ?? ""]
    .filter(Boolean)
    .join(" ");

  const collapseHeaderClass = [
    styles.collapseHeader,
    handPanelCollapsed ? styles.collapseHeaderCollapsed : "",
  ]
    .filter(Boolean)
    .join(" ");

  const chevronClass = [styles.chevron, handPanelCollapsed ? styles.chevronCollapsed : ""]
    .filter(Boolean)
    .join(" ");

  const countBadgeClass = [styles.countBadge, handCount >= MAX_SLOTS ? styles.countBadgeFull : ""]
    .filter(Boolean)
    .join(" ");

  // Build 5 slots (filled + empty to fill out to MAX_SLOTS)
  const slots: React.ReactElement[] = [];
  for (let i = 0; i < MAX_SLOTS; i++) {
    const card = hand[i];
    if (card !== undefined) {
      // R18-FC-5: per-card aura-active badge via full registry check.
      const auraActive = _cardHasActiveAura(card, seatIdx, auraState);
      slots.push(
        <SlotFilled
          key={card.instanceId}
          card={card}
          animDelay={i * 0.05}
          onOpen={(card_arg) => openCardDetail(card_arg, true)}
          auraActive={auraActive}
        />,
      );
    } else {
      slots.push(<SlotEmpty key={`empty-${i}`} animDelay={i * 0.05} />);
    }
  }

  const floatingStyle: React.CSSProperties =
    variant === "floating"
      ? {
          position: "fixed",
          top: "10vh",
          right: 0,
          width: "240px",
          height: "80vh",
          zIndex: 50,
          boxShadow: "-4px 0 16px rgba(0,0,0,0.5)",
          overflowY: "auto",
        }
      : {};

  // R10 #3: inject seat color CSS vars for collapsed gradient strip.
  const seatHex = SEAT_HEX_COLORS[seatIdx] ?? "#ffb420";
  // Alpha version of the seat color for the gradient (rgba string with 0.28 alpha).
  // Parse hex → rgb for the CSS var.
  const hexToRgba = (hex: string, a: number): string => {
    const h = hex.replace("#", "");
    const r = Number.parseInt(h.substring(0, 2), 16);
    const g = Number.parseInt(h.substring(2, 4), 16);
    const b = Number.parseInt(h.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  };
  const panelStyle: React.CSSProperties = {
    ...floatingStyle,
    "--seat-color": seatHex,
    "--seat-color-a": hexToRgba(seatHex, 0.28),
  } as React.CSSProperties;

  return (
    <div className={panelClass} style={panelStyle} data-testid="hand-panel">
      {/* Seat header — hidden when collapsed */}
      <div className={headerClass} data-testid="hand-panel-header">
        <div className={labelClass}>{SEAT_COLORS[seatIdx]} - ACTIVE</div>
        <div className={styles.seatSublabel}>{SEAT_DISPLAY_NAMES[seatIdx]}&apos;s Turn</div>
      </div>

      {/* Collapse toggle row — R10 #3: shows vertical "🎴 N/5" when collapsed */}
      <button
        type="button"
        className={collapseHeaderClass}
        onClick={toggleHandPanel}
        data-testid="hand-panel-collapse-toggle"
        aria-expanded={!handPanelCollapsed}
        title={
          handPanelCollapsed ? `Expand hand (${handCount}/${MAX_SLOTS} cards)` : "Collapse hand"
        }
      >
        {handPanelCollapsed ? (
          /* R10 #3: vertical label when collapsed */
          <span
            className={styles.collapseLabel}
            style={{ writingMode: "vertical-lr", transform: "rotate(180deg)", letterSpacing: 2 }}
            data-testid="hand-panel-collapsed-label"
          >
            🎴 {handCount}/{MAX_SLOTS}
          </span>
        ) : (
          <>
            <span className={styles.collapseLabel}>HAND</span>
            <div className={styles.collapseRight}>
              <div className={countBadgeClass} data-testid="hand-count-badge">
                {handCount}/{MAX_SLOTS}
              </div>
              <span className={chevronClass}>&#9660;</span>
            </div>
          </>
        )}
      </button>

      {/* R7 #1 — Active Auras strip above cards. AuraPanel deleted (6th
          relock); auras now surface here as a small summary row of the
          current player's hand-aura cards. Aura detection mirrors
          `_isAuraCard` in game-store: discardMode === "Whenever" OR any
          effect.kind === "AuraApply". */}
      {!handPanelCollapsed && hand.some((c) => isHandCardAura(c)) && (
        <div className={styles.auraStrip ?? ""} data-testid="hand-aura-strip">
          <span className={styles.auraStripLabel ?? ""}>⚡ Auras:</span>
          {hand
            .filter((c) => isHandCardAura(c))
            .map((c) => {
              const cd = getCardById(c.id);
              return (
                <button
                  type="button"
                  key={c.instanceId}
                  className={styles.auraStripChip ?? ""}
                  title={cd?.description ?? cd?.cardName ?? c.id}
                  data-testid={`hand-aura-chip-${c.instanceId}`}
                  onClick={() => openCardDetail(c, true)}
                >
                  {cd?.cardName ?? c.id}
                </button>
              );
            })}
        </div>
      )}

      {/* Hand slots body — hidden when collapsed */}
      <div
        className={[styles.handBody, handPanelCollapsed ? styles.handBodyHidden : ""]
          .filter(Boolean)
          .join(" ")}
        data-testid="hand-body"
      >
        <div className={styles.handSlots} data-testid="hand-slots">
          {slots}
        </div>
      </div>

      {/* Card count footer — hidden when collapsed */}
      <div
        className={[styles.footer, handPanelCollapsed ? styles.footerHidden : ""]
          .filter(Boolean)
          .join(" ")}
        data-testid="hand-panel-footer"
      >
        Cards: {handCount}/{MAX_SLOTS}
        {lastDiceRoll !== null && (
          <span style={{ marginLeft: "8px", color: "#FFB420" }}>🎲 {lastDiceRoll}</span>
        )}
      </div>

      {/* WEB.4.UI.18.PT (playtest #6): removed duplicate Roll Dice + End Turn buttons.
          PrimaryActionZone (fixed bottom-center) is the canonical primary-action surface. */}
    </div>
  );
}
